import {
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import {
	DeliveryPackageForm,
	DeliveryPackageFormProps,
} from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackageForm.tsx";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { AsyncButton, AsyncButtonProps } from "src/components/common/buttons/AsyncButton.tsx";
import {
	faBoxes,
	faCalculator,
	faCheck,
	faMinus,
	faPalletBoxes,
	faPrint,
	faShare,
	faTruckLoading,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoSelectField } from "../../../../common/inputFields/AavoSelectField.tsx";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import {
	AavoDataGridMobileViewDetailsViewRenderParams,
	AavoDataGridMobileViewDetailsViewTabsConfig,
	AavoDataGridRowContextMenuParams,
	TypedGridSortModel,
} from "src/components/common/dataGrid/types.tsx";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { DeliveryPackageLinesDataGrid } from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackageLinesDataGrid.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { ReactNode } from "react";
import {
	DeliveryPackagesDataGridApi,
	DeliveryPackagesDataGridApi_BundleHandling,
} from "src/api/generated/erp/delivery/api/deliveryPackagesDataGridApi.ts";
import { DeliveryPackageApi } from "src/api/generated/erp/delivery/api/deliveryPackageApi.ts";
import {
	DeliveryState,
	DeliveryStateValues,
	getDeliveryStateLabel,
	getDeliveryStateLabels,
} from "src/api/generated/erp/db/types/enums/deliveryState.ts";
import {
	DeliveryPackageState,
	getDeliveryPackageStateLabels,
} from "src/api/generated/erp/db/types/enums/deliveryPackageState.ts";
import { SelectSiteField } from "src/components/views/erp/common/sites/SelectSiteField.tsx";
import { DeliveryPackageSource } from "src/api/generated/erp/delivery/model/deliveryPackageSource.ts";
import { DeliveryFormWithPackages } from "src/components/views/erp/delivery/delivery/DeliveryFormWithPackages.tsx";
import {
	ParamsWithDataModelRequest,
	ServerSideDataGridModel,
} from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { DeliveryPackageBundleContentView } from "src/components/views/erp/delivery/deliveryPackageBundle/DeliveryPackageBundleContentView.tsx";
import { DeliveryPackageBundleCreationView } from "src/components/views/erp/delivery/deliveryPackageBundle/DeliveryPackageBundleCreationView.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { ServerSideDataModelResult } from "src/api/generated/common/dataGrids/serverSideDataModelResult.ts";
import { printFile } from "src/utils/filePrinting.ts";
import { filterNulls, getOnlyMember } from "src/utils/arrayUtils.ts";
import { DeliveryPackageWithSourcesView } from "src/api/generated/erp/db/types/tables/deliveryPackageWithSourcesView.ts";
import { DeliveryPackageSourceType } from "src/api/generated/io/aavo/applications/db/erp/types/deliveryPackageSourceType.ts";
import { AavoMenuDivider } from "src/components/common/menu/AavoMenuDivider.tsx";
import { RefreshableElementRef } from "../../../../../utils/useRefreshRef.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveryPackagesDataGridProps {
	gridId: string;
	fetchData?: (
		params: ParamsWithDataModelRequest<DeliveryPackagesDataGridFetchParams>,
	) => Promise<ServerSideDataModelResult<DeliveryPackageWithSourcesView>>;
	onSelectedPackageChanged?: (row: DeliveryPackageWithSourcesView[] | []) => void;
	deliveryPackageSourceType?: DeliveryPackageSourceType;
	deliveryPackageSource?: DeliveryPackageSource;
	deliveryId?: number | null;
	onlyPackagesWithoutConnectedDelivery?: boolean;
	packageStates?: DeliveryPackageState[];
	deliveryPackageId?: number | null;
	parentPackageBundleId?: number | null;
	showSourceDocumentsButton?: boolean;
	showSearchField?: boolean;
	showSiteField?: boolean;
	showDeliveryStateSelection?: boolean;
	showCalculateWeightButton?: boolean;
	printPackageLabelButtonLocation?: "rowContextMenu" | "actionBar";
	removeFromDeliveryButtonLocation?: "actionBar" | "rowContextMenu";
	stateChangeButtonsLocation?: "rowContextMenu" | "actionBar";
	sortModel?: TypedGridSortModel<DeliveryPackageWithSourcesView>;
	addEnabled?: boolean;
	onStateChanged?: () => void;
	onPackagesRemovedFromDelivery?: () => void | Promise<unknown>;
	disablePagination?: boolean;
	actionBarExtraComponents?: ReactNode;
	rowContextMenuExtraComponents?: (
		params: AavoDataGridRowContextMenuParams<DeliveryPackageWithSourcesView>,
	) => ReactNode[];
	checkboxSelection?: boolean;
	readonly?: boolean;
	bundleHandling?: DeliveryPackagesDataGridApi_BundleHandling;
	showBundleCreationButton?: boolean;
	defaultSiteId?: number | null;
	createdByUserId?: number | null;
	refreshRef?: RefreshableElementRef<DeliveryPackagesDataGridFetchParams>;
	/**
	 * Lists the packages of [deliveryId] read-only through the cargo driver API, which accepts only deliveries of the
	 * driver's own transports.
	 */
	cargoDriverView?: boolean;
}

export interface DeliveryPackagesDataGridFetchParams {
	searchQuery: string;
	deliveryState: DeliveryState | undefined | null;
	customerId: number | undefined | null;
	siteId: number | undefined | null;
	createdByUserId: number | undefined | null;
}

export const DeliveryPackagesDataGrid = ({
	gridId,
	onSelectedPackageChanged,
	packageStates = [],
	deliveryPackageSource,
	deliveryPackageSourceType,
	deliveryId,
	onlyPackagesWithoutConnectedDelivery,
	deliveryPackageId = null,
	refreshRef,
	onStateChanged,
	onPackagesRemovedFromDelivery,
	addEnabled = false,
	showDeliveryStateSelection = false,
	showSearchField = false,
	showCalculateWeightButton = false,
	stateChangeButtonsLocation = "rowContextMenu",
	showSourceDocumentsButton = false,
	printPackageLabelButtonLocation = "rowContextMenu",
	removeFromDeliveryButtonLocation = "rowContextMenu",
	disablePagination = false,
	actionBarExtraComponents = null,
	rowContextMenuExtraComponents,
	readonly = false,
	bundleHandling = "ONLY_TOP_LEVEL",
	parentPackageBundleId = null,
	showSiteField = false,
	showBundleCreationButton,
	checkboxSelection = false,
	defaultSiteId,
	fetchData,
	createdByUserId: createdByUserIdProp,
	sortModel,
	cargoDriverView = false,
}: DeliveryPackagesDataGridProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const { openDialog } = useGenericDialog();
	const showConfirmDialog = useConfirmDialog();
	const hasPermission = useHasPermission();
	const editDeliveryPackagesPermission = hasPermission(Permissions.Delivery.DeliveryPackages.MANAGE);
	const readCustomerOrdersPermission = hasPermission(Permissions.Sales.CustomerOrders.READ);

	return (
		<ServerSideDataGridModel<
			ServerSideDataModelResult<DeliveryPackageWithSourcesView>,
			DeliveryPackageWithSourcesView,
			DeliveryPackagesDataGridFetchParams
		>
			customFunctionUiActionView={"DELIVERY_PACKAGES"}
			fetchData={(params) =>
				fetchData != null ? fetchData(params)
				: cargoDriverView && deliveryId != null ?
					DeliveryPackagesDataGridApi.getDeliveryPackagesForCargoDriver({
						deliveryId: deliveryId,
						searchQuery: params.searchQuery,
						dataModelRequest: params.dataModelRequest,
					})
				:	DeliveryPackagesDataGridApi.getDeliveryPackages({
						source: deliveryPackageSource,
						sourceType: deliveryPackageSourceType,
						deliveryId: deliveryId,
						packageState: packageStates,
						deliveryPackageId: deliveryPackageId,
						onlyPackagesWithoutConnectedDelivery: onlyPackagesWithoutConnectedDelivery,
						bundleHandling: bundleHandling,
						parentPackageBundleId: parentPackageBundleId,
						...params,
					})
			}
			initialParams={{
				searchQuery: "",
				deliveryState: genericNullableValue<DeliveryState>(),
				customerId: genericNullableValue<number>(),
				siteId: genericNullableValue<number>(defaultSiteId),
				createdByUserId: createdByUserIdProp,
			}}
			getDataModelResult={(response) => response}
			getRowId={(row) => row.deliveryPackageId}
			onSelectionChanged={(selectedRows) => {
				onSelectedPackageChanged?.(selectedRows);
			}}
			usePagination={!disablePagination}
			selectFirstRowOnLoad={!checkboxSelection}
			gridId={gridId + "_v3"}
			refreshRef={refreshRef}
			defaultGridState={{
				sorting: {
					sortModel: sortModel,
				},
			}}
			render={({ dataGridProps, refreshData, currentParams, onlySelectedRow, selectedRows }) => (
				<FormCrudDataGrid<DeliveryPackageWithSourcesView>
					columns={[
						deliveryPackageSource == null && [
							textColumn({
								field: "sourceIds",
								headerName:
									deliveryPackageSourceType === "PROJECT" ? i18n.t("project_no")
									: deliveryPackageSourceType === "CUSTOMER_ORDER" ? i18n.t("order_no")
									: i18n.t("source_id"),
								width: 80,
							}),
							textColumn({
								field: "sourceDescriptions",
								headerName:
									deliveryPackageSourceType === "PROJECT" ? i18n.t("project")
									: deliveryPackageSourceType === "CUSTOMER_ORDER" ? i18n.t("customer_order")
									: i18n.t("source"),
								width: 200,
							}),
							textColumn({
								field: "sourceDeliveryAddressConcatenated",
								headerName: i18n.t("address"),
								width: 200,
							}),
						],
						integerColumn({
							field: "packageNo",
							headerName: i18n.t("package_no"),
							width: 80,
							mobile: "header",
						}),
						textColumn({
							field: "description",
							headerName: i18n.t("description"),
							width: 300,
							mobile: "header",
						}),
						textColumn({
							field: "packageTypeName",
							headerName: i18n.t("type"),
							mobile: "cell",
						}),
						enumColumn({
							field: "state",
							headerName: i18n.t("state"),
							enumLabels: getDeliveryPackageStateLabels(),
							width: 125,
							mobile: "cell",
						}),
						textColumn({
							field: "warehouseLocationCode",
							headerName: i18n.t("warehouse_location_short"),
							width: 80,
							mobile: "cell",
						}),
						floatColumn({
							field: "weight",
							headerName: i18n.t("weight"),
							width: 75,
							mobile: "cell",
						}),
						floatColumn({
							field: "length",
							headerName: i18n.t("length"),
							width: 75,
							mobile: "cell",
						}),
						floatColumn({
							field: "width",
							headerName: i18n.t("width"),
							width: 75,
							mobile: "cell",
						}),
						floatColumn({
							field: "height",
							headerName: i18n.t("height"),
							width: 75,
							mobile: "cell",
						}),
						enumColumn({
							field: "deliveryState",
							headerName: i18n.t("delivery_state"),
							enumLabels: getDeliveryStateLabels(),
							width: 125,
						}),
						textColumn({
							field: "sourceDeliveryPostalCode",
							headerName: i18n.t("postal_code"),
						}),
						textColumn({
							field: "sourceDeliveryCity",
							headerName: i18n.t("city"),
						}),
						textColumn({
							field: "createdByUserName",
							headerName: i18n.t("created_by"),
						}),
						dateTimeColumn({
							field: "createdAt",
							headerName: i18n.t("created_at"),
							width: 150,
						}),
						dateTimeColumn({
							field: "pickedAt",
							headerName: i18n.t("picked_at"),
							width: 150,
						}),
						integerColumn({
							field: "deliveryPackageId",
							headerName: i18n.t("id"),
							width: 75,
						}),
					]}
					listViewOnMobile={{
						detailsView: (params) => mobileViewDetailsTabs({ ...params, deliveryPackageSource, cargoDriverView }),
					}}
					form={{
						dialogSize: "md",
						dialogTitle: i18n.t("package"),
						addRowEnabled: editDeliveryPackagesPermission && addEnabled && !readonly,
						editEnabled: !cargoDriverView && editDeliveryPackagesPermission,
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.delivery.deliveryPackage.formDialog"),
							title: i18n.t("package"),
							data: {
								deliveryPackageId: row?.deliveryPackageId ?? null,
								deliveryId: row?.deliveryId ?? deliveryId ?? null,
								sourceIds: row?.sourceIds ?? null,
							},
						}),
						component: ({ row, onCompleted, onFormEdited }) => {
							const initialization: DeliveryPackageFormProps["initialization"] =
								row == null ?
									{
										type: "newPackage",
										deliveryId: deliveryId,
										deliveryPackageSources: filterNulls([deliveryPackageSource]),
									}
								:	{
										type: "existingPackage",
										deliveryPackageId: row.deliveryPackageId,
									};

							return (
								<DeliveryPackageForm
									initialization={initialization}
									onCompleted={(result) => {
										onCompleted(result);
									}}
									onFormEdited={onFormEdited}
									readonly={readonly || row?.state === "DELIVERED"}
								/>
							);
						},
					}}
					remove={
						!readonly && editDeliveryPackagesPermission && deliveryId == null ?
							{
								type: "enabled",
								action: async (params) => {
									await DeliveryPackagesDataGridApi.delete_({
										deliveryPackages: params.items,
									});
								},
							}
						:	undefined
					}
					actionBarComponents={
						<>
							{showSiteField && deliveryId == null && deliveryPackageSource == null && (
								<SelectSiteField
									value={currentParams.siteId}
									onChange={(siteId) => refreshData({ siteId })}
								/>
							)}
							{showDeliveryStateSelection && (
								<AavoSelectField
									label={i18n.t("delivery_state")}
									options={DeliveryStateValues}
									getOptionKey={(option) => option}
									getOptionLabel={(option) => getDeliveryStateLabel(option)}
									onChange={async (value) => {
										await refreshData({ deliveryState: value });
									}}
								/>
							)}
							{showSearchField && (
								<AavoTextField
									label={i18n.t("search")}
									onSubmit={async (input) => {
										await refreshData({ searchQuery: input });
									}}
								/>
							)}
							{removeFromDeliveryButtonLocation === "actionBar" &&
								deliveryId != null &&
								!readonly &&
								editDeliveryPackagesPermission && (
									<AsyncButton
										label={i18n.t("remove_from_delivery")}
										icon={faMinus}
										variant="outlined"
										disabled={selectedRows.length === 0}
										onClick={() => onRemovePackagesFromDelivery(selectedRows, refreshData)}
									/>
								)}
							{printPackageLabelButtonLocation === "actionBar" && onlySelectedRow && (
								<AsyncButton
									icon={faPrint}
									label={i18n.t("package_label")}
									variant={"outlined"}
									onClick={async () => {
										if (!onlySelectedRow) return;
										const fileHandle =
											cargoDriverView ?
												await DeliveryPackageApi.printDeliveryPackageLabelForCargoDriver({
													deliveryPackageId: onlySelectedRow.deliveryPackageId,
												})
											:	await DeliveryPackageApi.printDeliveryPackageLabel({
													deliveryPackageId: onlySelectedRow.deliveryPackageId,
												});
										printFile(fileHandle);
									}}
								/>
							)}
							{showCalculateWeightButton && editDeliveryPackagesPermission && (
								<AsyncButton
									icon={faCalculator}
									label={i18n.t("calculate_weight")}
									variant={"outlined"}
									disabled={() => {
										if (!onlySelectedRow) return i18n.t("select_line");
										return false;
									}}
									onClick={async () => {
										if (!onlySelectedRow) return;
										await DeliveryPackageApi.calculateDeliveryPackageWeight({
											deliveryPackageId: onlySelectedRow.deliveryPackageId,
										});
										await refreshData();
									}}
								/>
							)}
							{stateChangeButtonsLocation === "actionBar" && onlySelectedRow && (
								<SetStateButton
									row={onlySelectedRow}
									onPackageStateChanged={onStateChanged}
									refreshData={refreshData}
									variant="outlined"
								/>
							)}
							{showBundleCreationButton &&
								editDeliveryPackagesPermission &&
								tenantConfig.erp.deliveryPackageBundlingEnabled && (
									<AsyncButton
										icon={faPalletBoxes}
										label={i18n.t("delivery.create_bundle")}
										variant="outlined"
										onClick={async () => {
											const deliverySourceAddress =
												deliveryPackageSource == null ? undefined : (
													await DeliveryPackageApi.getDeliveryPackageSourceAddress({
														deliveryPackageSource,
													})
												);
											openDialog(({ closeDialog }) => ({
												title: i18n.t("delivery.new_bundle"),
												size: "sm",
												fullScreenOnMobile: true,
												agentContext: {
													key: defineAgentUiContextKey("erp.delivery.deliveryPackageBundleCreation.dialog"),
													title: i18n.t("delivery.new_bundle"),
													data: {
														selectedDeliveryPackageIds: selectedRows.map((row) => row.deliveryPackageId),
														sourceType: deliveryPackageSource?.type ?? null,
														customerOrderId:
															deliveryPackageSource?.type === "customerOrder" ?
																deliveryPackageSource.customerOrderId
															:	null,
														projectId:
															deliveryPackageSource?.type === "project" ? deliveryPackageSource.projectId : null,
													},
												},
												content: (
													<DeliveryPackageBundleCreationView
														initialDeliveryAddress={deliverySourceAddress ?? undefined}
														initialSelectedPackageIds={selectedRows?.map(
															(r) => r.deliveryPackageId,
														)}
														noStorage
														onCompleted={async () => {
															await closeDialog();
															await refreshData();
														}}
													/>
												),
											}));
										}}
									/>
								)}
							{actionBarExtraComponents}
						</>
					}
					rowContextMenuComponents={(contextMenuParams) => {
						const { row, onlySingleRowSelected, allSelectedRows } = contextMenuParams;
						const onlyConnectedCustomerOrderId = getOnlyMember(row.sourceCustomerOrderIds);
						return [
							onlySingleRowSelected && onlyConnectedCustomerOrderId != null && (
								<OpenCustomerOrderButton
									key={"openCustomerOrder"}
									customerOrderId={onlyConnectedCustomerOrderId}
								/>
							),
							readCustomerOrdersPermission &&
								showSourceDocumentsButton &&
								onlySingleRowSelected &&
								onlyConnectedCustomerOrderId != null && (
									<DocumentsOfObjectButton
										key={"orderDocuments"}
										label={i18n.t("order_documents")}
										objectRef={{
											objectType: "CUSTOMER_ORDER",
											objectId: onlyConnectedCustomerOrderId,
										}}
										editable={"onlyOfficeForExistingDocuments"}
										variant={"menu"}
									/>
								),
							!cargoDriverView && row.deliveryId != null && (
								<AsyncMenuButton
									key={"openDelivery"}
									icon={faTruckLoading}
									label={i18n.t("open_delivery")}
									onClick={() => {
										openDialog(({ closeDialog }) => ({
											title: i18n.t("delivery"),
											agentContext: {
												key: defineAgentUiContextKey("erp.delivery.deliveryFromPackage.dialog"),
												title: i18n.t("delivery"),
												pathSegments: [`${i18n.t("delivery")} ${row.deliveryId}`],
												data: {
													deliveryId: row.deliveryId ?? null,
													deliveryPackageId: row.deliveryPackageId,
												},
											},
											content: (
												<DeliveryFormWithPackages
													deliveryId={row.deliveryId!}
													readonly
													onFormCompleted={() => closeDialog()}
												/>
											),
										}));
									}}
								/>
							),
							!cargoDriverView && row.isBundle && (
								<AsyncMenuButton
									key={"openBundlePackages"}
									label={i18n.t("delivery.bundle_packages")}
									icon={faPalletBoxes}
									onClick={() => {
										openDialog(() => ({
											title: i18n.t("delivery.bundle_packages"),
											size: "fullscreen",
											agentContext: {
												key: defineAgentUiContextKey("erp.delivery.deliveryPackageBundleContent.dialog"),
												title: i18n.t("delivery.bundle_packages"),
												pathSegments: [`${i18n.t("package")} ${row.packageNo}`],
												data: {
													deliveryPackageId: row.deliveryPackageId,
													packageNo: row.packageNo,
												},
											},
											content: (
												<DeliveryPackageBundleContentView
													packageBundle={row}
													refreshParent={() => refreshData()}
												/>
											),
										}));
									}}
								/>
							),
							!row.isBundle && (
								<AsyncMenuButton
									key={"openPackageLines"}
									label={i18n.t("package_lines")}
									icon={faShare}
									onClick={() => {
										openDialog(() => ({
											title: i18n.t("package_lines"),
											size: "fullscreen",
											agentContext: {
												key: defineAgentUiContextKey("erp.delivery.deliveryPackageLines.dialog"),
												title: i18n.t("package_lines"),
												pathSegments: [`${i18n.t("package")} ${row.packageNo}`],
												data: {
													deliveryPackageId: row.deliveryPackageId,
													packageNo: row.packageNo,
												},
											},
											content: (
												<DeliveryPackageLinesDataGrid
													deliveryPackage={row}
													deliveryPackageSource={deliveryPackageSource}
													cargoDriverView={cargoDriverView}
												/>
											),
										}));
									}}
								/>
							),
							!cargoDriverView &&
								stateChangeButtonsLocation === "rowContextMenu" &&
								onlySingleRowSelected && [
									<AavoMenuDivider key={"divider1"} />,
									<SetStateButton
										key={"setState"}
										row={row}
										onPackageStateChanged={onStateChanged}
										refreshData={refreshData}
										variant="menu"
									/>,
								],
							removeFromDeliveryButtonLocation === "rowContextMenu" &&
								deliveryId != null &&
								!readonly &&
								!cargoDriverView &&
								editDeliveryPackagesPermission && (
									<AsyncMenuButton
										key={"removeFromDelivery"}
										label={i18n.t("remove_from_delivery")}
										icon={faMinus}
										onClick={() =>
											onRemovePackagesFromDelivery(
												allSelectedRows.length > 0 ? allSelectedRows : [row],
												refreshData,
											)
										}
									/>
								),
							<AavoMenuDivider key={"divider2"} />,
							row.parentPackageBundleId == null && (
								<AsyncMenuButton
									key={"printShippingLabel"}
									icon={faPrint}
									label={i18n.t("shipping_label")}
									onClick={async () => {
										const fileHandle =
											cargoDriverView ?
												await DeliveryPackageApi.printDeliveryPackageShippingLabelsForCargoDriver({
													deliveryPackageIds: [row.deliveryPackageId],
												})
											:	await DeliveryPackageApi.printDeliveryPackageShippingLabels({
													deliveryPackageIds: [row.deliveryPackageId],
												});
										printFile(fileHandle);
									}}
								/>
							),
							printPackageLabelButtonLocation === "rowContextMenu" && onlySingleRowSelected && (
								<AsyncMenuButton
									icon={faPrint}
									label={i18n.t("package_label")}
									onClick={async () => {
										const fileHandle =
											cargoDriverView ?
												await DeliveryPackageApi.printDeliveryPackageLabelForCargoDriver({
													deliveryPackageId: row.deliveryPackageId,
												})
											:	await DeliveryPackageApi.printDeliveryPackageLabel({
													deliveryPackageId: row.deliveryPackageId,
												});
										printFile(fileHandle);
									}}
								/>
							),
							rowContextMenuExtraComponents?.(contextMenuParams),
						];
					}}
					checkboxSelection={checkboxSelection}
					agentContext={{
						key: defineAgentUiContextKey("erp.delivery.deliveryPackages.grid"),
						data: {
							deliveryId: deliveryId ?? null,
							deliveryPackageSourceType: deliveryPackageSourceType ?? null,
							customerOrderId:
								deliveryPackageSource?.type === "customerOrder" ? deliveryPackageSource.customerOrderId : null,
							projectId: deliveryPackageSource?.type === "project" ? deliveryPackageSource.projectId : null,
						},
						rowFields: ["deliveryPackageId", "deliveryId", "sourceCustomerOrderIds", "sourceProjectIds"],
						selectedRowPathSegment: (row) => `${i18n.t("package")} ${row.packageNo}`,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);

	async function onRemovePackagesFromDelivery(
		packagesToRemove: DeliveryPackageWithSourcesView[],
		refreshData: () => Promise<unknown>,
	) {
		const alreadyLoadedPackages = packagesToRemove.filter((row) => row.loadedAt != null);
		if (alreadyLoadedPackages.length > 0) {
			const confirmed = await showConfirmDialog({
				title: i18n.t("warning"),
				message: i18n.t("remove_packages_from_delivery_confirm_loaded_packages", {
					packageNumbers: alreadyLoadedPackages.map((row) => row.packageNo).join(", "),
				}),
			});
			if (!confirmed) return;
		}
		await DeliveryPackageApi.removePackagesFromDelivery({
			deliveryPackageIds: packagesToRemove.map((row) => row.deliveryPackageId),
		});
		await Promise.all([refreshData(), onPackagesRemovedFromDelivery?.()]);
	}
};

interface SetStateButtonProps extends Pick<AsyncButtonProps, "disabled" | "variant"> {
	row: DeliveryPackageWithSourcesView;
	onPackageStateChanged?: () => void;
	refreshData: () => Promise<unknown>;
}

const SetStateButton = ({ row, onPackageStateChanged, refreshData, variant, disabled }: SetStateButtonProps) => {
	const hasPermission = useHasPermission();
	const editDeliveryPackagesPermission = hasPermission(Permissions.Delivery.DeliveryPackages.MANAGE);
	if (!editDeliveryPackagesPermission) return null;

	if (row.state !== "INITIAL" && row.state !== "COMPLETED") return null;

	const isInitial = row.state === "INITIAL";

	return (
		<AsyncButton
			variant={variant}
			label={isInitial ? i18n.t("mark_picked") : i18n.t("return_open")}
			icon={isInitial ? faCheck : faUndo}
			disabled={disabled}
			onClick={async () => {
				if (isInitial) {
					await DeliveryPackageApi.setPackageStateToPicked({
						deliveryPackageId: row.deliveryPackageId,
					});
				} else {
					await DeliveryPackageApi.setPackageStateToUnpicked({
						deliveryPackageId: row.deliveryPackageId,
					});
				}
				onPackageStateChanged?.();
				await refreshData();
			}}
		/>
	);
};

interface MobileViewDetailsTabsParams extends AavoDataGridMobileViewDetailsViewRenderParams<DeliveryPackageWithSourcesView> {
	deliveryPackageSource: DeliveryPackageSource | undefined;
	cargoDriverView: boolean;
}

const mobileViewDetailsTabs = ({
	row,
	deliveryPackageSource,
	cargoDriverView,
}: MobileViewDetailsTabsParams): AavoDataGridMobileViewDetailsViewTabsConfig => {
	return {
		type: "tabs",
		items: [
			!cargoDriverView &&
				row.isBundle && {
				title: i18n.t("delivery.bundle_packages"),
				icon: faPalletBoxes,
				content: <DeliveryPackageBundleContentView packageBundle={row} />,
			},
			{
				title: i18n.t("package_lines"),
				icon: faBoxes,
				content: (
					<DeliveryPackageLinesDataGrid
						deliveryPackage={row}
						deliveryPackageSource={deliveryPackageSource}
						cargoDriverView={cargoDriverView}
					/>
				),
			},
		],
	};
};
